// Chart data transforms: friendly date labels (179), chart sorting (182),
// range colors (180), tooltip styling (181), highlight dimming (168).
import { smartComparator } from "@/lib/filterValues";

const MONTHS_S = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_L = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

// "2025-01" -> "Jan 2025", "2025-Q1"/"2025Q1" -> "Q1 2025", "2025-W03" -> "W03 2025",
// "2025-01-15" -> "15 Jan" (item 35), "2025-01-15 14:00" -> "15 Jan 14:00"
export function prettyDateLabel(v, style = "auto") {
  if (style === "raw" || v == null) return v;
  const s = String(v);
  let m = /^(\d{4})-(\d{2})$/.exec(s);
  if (m) {
    const mi = Number(m[2]) - 1;
    if (mi < 0 || mi > 11) return v;
    return style === "month_yyyy" ? `${MONTHS_L[mi]} ${m[1]}` : `${MONTHS_S[mi]} ${m[1]}`;
  }
  m = /^(\d{4})[-]?Q([1-4])$/i.exec(s);
  if (m) return `Q${m[2]} ${m[1]}`;
  m = /^(\d{4})-W(\d{1,2})$/i.exec(s);
  if (m) return `W${m[2].padStart(2, "0")} ${m[1]}`;
  m = /^(\d{4})-(\d{2})-(\d{2})\/\d{4}-\d{2}-\d{2}$/.exec(s);   // week bucket -> week commencing
  if (m) {
    const mi = Number(m[2]) - 1;
    if (mi < 0 || mi > 11) return v;
    return style === "auto" ? `W/c ${m[3]} ${MONTHS_S[mi]}` : `W/c ${m[3]} ${MONTHS_S[mi]} ${m[1]}`;
  }
  m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(s);
  if (m) {
    const mi = Number(m[2]) - 1;
    if (mi < 0 || mi > 11) return v;
    const day = `${m[3]} ${MONTHS_S[mi]}`;
    return `${style === "auto" ? day : `${day} ${m[1]}`} ${m[4]}:${m[5]}`;
  }
  m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) {
    const mi = Number(m[2]) - 1;
    if (mi < 0 || mi > 11) return v;
    if (style === "d_mon_yyyy" || style === "month_yyyy") return `${Number(m[3])} ${MONTHS_S[mi]} ${m[1]}`;
    return `${m[3]} ${MONTHS_S[mi]}`;
  }
  return v;
}

const PRETTY_DATE_RE = /^\d{4}-\d{2}$|^\d{4}-?Q[1-4]$|^\d{4}-W\d{1,2}$|^\d{4}-\d{2}-\d{2}(\/\d{4}-\d{2}-\d{2}|[ T]\d{2}:\d{2})?/i;

// Rewrites the x-dimension values of the result set with friendly date labels.
export function applyDateLabels(data, spec, opts = {}) {
  const style = opts.dateLabelFormat || "auto";
  if (style === "raw" || !data || !data.rows?.length || !spec?.x_axis) return data;
  const xk = spec.x_axis;
  const sample = data.rows.find((r) => r[xk] != null);
  if (!sample) return data;
  if (!PRETTY_DATE_RE.test(String(sample[xk]))) return data;
  // Item 9: keep label -> raw value so drill-down / cross-filter still send the queryable value
  const rawByLabel = {};
  const rows = data.rows.map((r) => {
    const label = prettyDateLabel(r[xk], style);
    if (label != null && r[xk] != null) rawByLabel[String(label)] = r[xk];
    return { ...r, [xk]: label };
  });
  return { ...data, rows, rawByLabel };
}

// Client-side chart sorting without touching the dataset.
// Supports single (opts.sortBy/sortMeasure) + multi-level (opts.sortThen) sorting.
export function applyChartSort(data, spec, opts = {}) {
  const by = opts.sortBy;
  if (!by || by === "none" || !data || !data.rows?.length) return data;
  const xk = spec.x_axis;
  const cols = data.columns || [];
  const mkOf = (want) => (want && cols.includes(want)) ? want : (spec.y_axis || [])[0];
  const cmpFor = (mode, measure) => {
    const mk = mkOf(measure);
    // Item 35: category order respects months / quarters / weekdays / week numbers chronologically
    if (mode === "cat_asc" || mode === "cat_desc") {
      const cmp = smartComparator(data.rows.map((r) => r[xk]));
      return mode === "cat_asc" ? (a, b) => cmp(a[xk], b[xk]) : (a, b) => cmp(b[xk], a[xk]);
    }
    if (mode === "measure_asc" && mk) return (a, b) => (Number(a[mk]) || 0) - (Number(b[mk]) || 0);
    if (mode === "measure_desc" && mk) return (a, b) => (Number(b[mk]) || 0) - (Number(a[mk]) || 0);
    return null;
  };
  const levels = [cmpFor(by, opts.sortMeasure)];
  const then = opts.sortThen || {};
  if (then.by && then.by !== "none") levels.push(cmpFor(then.by, then.measure));
  const cmps = levels.filter(Boolean);
  if (!cmps.length) return data;
  const rows = [...data.rows].sort((a, b) => {
    for (const c of cmps) {
      const r = c(a, b);
      if (r !== 0) return r;
    }
    return 0;
  });
  return { ...data, rows };
}

// ---- Range colors (item 180) ----
function hex2rgb(h) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(h || "");
  return m ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)] : null;
}
function lerpColor(c1, c2, t) {
  const a = hex2rgb(c1), b = hex2rgb(c2);
  if (!a || !b) return c1;
  const mix = a.map((v, i) => Math.round(v + (b[i] - v) * Math.max(0, Math.min(1, t))));
  return `rgb(${mix[0]},${mix[1]},${mix[2]})`;
}

export function activeRanges(opts) {
  return (opts.rangeColors || []).filter((r) => r.enabled !== false && r.colors?.length &&
    r.start !== "" && r.start != null && r.end !== "" && r.end != null && Number(r.end) >= Number(r.start));
}

// Inclusive/exclusive boundary support: r.bounds is "[]" (default), "[)", "(]" or "()".
function inRange(v, r) {
  const s = Number(r.start), e = Number(r.end);
  const b = r.bounds || "[]";
  return (b[0] === "(" ? v > s : v >= s) && (b[1] === ")" ? v < e : v <= e);
}

export function rangeColorOf(value, ranges) {
  const v = Number(value);
  if (!Number.isFinite(v)) return null;
  for (const r of ranges) {
    if (inRange(v, r)) {
      const s = Number(r.start), e = Number(r.end);
      if (r.colors.length === 1) return r.colors[0];
      const t = e === s ? 0 : (v - s) / (e - s);
      return lerpColor(r.colors[0], r.colors[r.colors.length - 1], t);
    }
  }
  return null;
}

// v2: returns {color, opacity} where color may be an ECharts gradient object.
export function rangeStyleOf(value, ranges) {
  const v = Number(value);
  if (!Number.isFinite(v)) return null;
  for (const r of ranges) {
    const s = Number(r.start), e = Number(r.end);
    if (inRange(v, r)) {
      const gt = r.gradientType || "";
      const cs = r.colors || [];
      let color;
      if (gt === "linear" && cs.length > 1) {
        color = { type: "linear", x: 0, y: 0, x2: 0, y2: 1, colorStops: cs.map((c, i) => ({ offset: i / (cs.length - 1), color: c })) };
      } else if (gt === "radial" && cs.length > 1) {
        color = { type: "radial", x: 0.5, y: 0.5, r: 0.8, colorStops: cs.map((c, i) => ({ offset: i / (cs.length - 1), color: c })) };
      } else if (cs.length > 1) {
        const t = e === s ? 0 : (v - s) / (e - s);
        color = lerpColor(cs[0], cs[cs.length - 1], t);
      } else {
        color = cs[0];
      }
      const st = { color };
      if (r.opacity != null && r.opacity !== "" && Number(r.opacity) < 1) st.opacity = Number(r.opacity);
      return st;
    }
  }
  return null;
}

// Adds hidden series so range labels show in the legend with their colors.
export function rangeLegendSeries(ranges) {
  return ranges.map((r) => ({
    name: r.label || `${r.start}–${r.end}`, type: "bar", data: [],
    itemStyle: { color: r.colors[0] }, silent: true,
  }));
}

// ---- Tooltip style module (item 181) ----
export function applyTooltipStyle(option, ts) {
  if (!option || !ts || !Object.keys(ts).length) return option;
  const apply = (tt) => {
    if (!tt) return tt;
    const out = { ...tt, confine: true };
    if (ts.enabled === false) out.show = false;
    if (ts.bg) out.backgroundColor = ts.bg;
    if (ts.borderColor) out.borderColor = ts.borderColor;
    if (ts.borderWidth != null && ts.borderWidth !== "") out.borderWidth = Number(ts.borderWidth);
    if (ts.padding != null && ts.padding !== "") out.padding = Number(ts.padding);
    out.textStyle = {
      ...(tt.textStyle || {}),
      ...(ts.color ? { color: ts.color } : {}),
      ...(ts.fontSize ? { fontSize: Number(ts.fontSize) } : {}),
      ...(ts.fontFamily ? { fontFamily: ts.fontFamily } : {}),
    };
    const css = [];
    if (ts.radius != null && ts.radius !== "") css.push(`border-radius:${Number(ts.radius)}px`);
    if (ts.shadow === false) css.push("box-shadow:none");
    if (ts.opacity != null && ts.opacity !== "" && Number(ts.opacity) < 1) css.push(`opacity:${Number(ts.opacity)}`);
    if (css.length) out.extraCssText = css.join(";") + ";";
    if (ts.position && ts.position !== "auto") out.position = ts.position === "pointer" ? undefined : ts.position;
    return out;
  };
  option.tooltip = apply(option.tooltip || {});
  return option;
}

// ---- Highlight mode (items 168 + cross-highlight): dim non-matching data points ----
export function applyHighlightToOption(option, spec, highlights = []) {
  if (!option || !highlights.length) return option;
  const xk = spec?.x_axis;
  const hs = highlights.filter((h) => h.field === xk || h.key === spec?.x_axis);
  if (!hs.length || !Array.isArray(option.series)) return option;
  // OR within the same field, AND across different fields (Power BI semantics)
  const byField = {};
  hs.forEach((h) => {
    const k = h.field || h.key;
    const vals = Array.isArray(h.value) ? h.value.map(String) : [String(h.value)];
    byField[k] = [...(byField[k] || []), ...vals];
  });
  const groups = Object.values(byField);
  const match = (name) => groups.every((vals) => vals.includes(String(name)));
  option.series = option.series.map((s) => {
    if (s.type === "pie" || s.type === "funnel") {
      return { ...s, data: (s.data || []).map((d) => match(d.name) ? d : { ...d, itemStyle: { ...(d.itemStyle || {}), opacity: 0.22 } }) };
    }
    if (["bar", "line", "scatter", "pictorialBar"].includes(s.type)) {
      const cats = (Array.isArray(option.xAxis) ? option.xAxis[0] : option.xAxis)?.data
        || (Array.isArray(option.yAxis) ? option.yAxis[0] : option.yAxis)?.data || [];
      return {
        ...s,
        data: (s.data || []).map((d, i) => {
          const name = cats[i];
          if (name === undefined || match(name)) return d;
          const val = (d && typeof d === "object" && !Array.isArray(d)) ? d : { value: d };
          return { ...val, itemStyle: { ...(val.itemStyle || {}), opacity: 0.18 } };
        }),
      };
    }
    return s;
  });
  return option;
}

// ---- Analytics overlays: reference lines + trendlines (Batch 4) ----
const _fmtShort = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  if (Math.abs(n) >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (Math.abs(n) >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

export function applyRefLines(option, opts, horizontal = false) {
  const lines = (opts.refLines || []).filter((r) => r.enabled !== false);
  if (!lines.length || !Array.isArray(option.series) || !option.series.length) return;
  const s0 = option.series.find((s) => ["bar", "line", "scatter"].includes(s.type) && Array.isArray(s.data));
  if (!s0) return;
  const vals = s0.data.map((d) => Number(Array.isArray(d) ? d[1] : (d && typeof d === "object" ? d.value : d))).filter(Number.isFinite);
  const calc = (mode, v) => {
    if (!mode || mode === "constant") return Number(v);
    if (!vals.length) return null;
    if (mode === "avg") return vals.reduce((a, b) => a + b, 0) / vals.length;
    if (mode === "min") return Math.min(...vals);
    if (mode === "max") return Math.max(...vals);
    if (mode === "median") { const s = [...vals].sort((a, b) => a - b); const m = Math.floor(s.length / 2); return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2; }
    return null;
  };
  const data = lines.map((r) => {
    const v = calc(r.mode, r.value);
    if (v == null || !Number.isFinite(v)) return null;
    const name = r.label || `${{ constant: "Target", avg: "Avg", min: "Min", max: "Max", median: "Median" }[r.mode || "constant"] || ""}: ${_fmtShort(v)}`;
    return {
      [horizontal ? "xAxis" : "yAxis"]: v,
      label: { formatter: name, position: "insideEndTop", color: r.color || "#FF6B35", fontSize: 10 },
      lineStyle: { color: r.color || "#FF6B35", type: r.dash || "dashed", width: 1.5 },
    };
  }).filter(Boolean);
  if (!data.length) return;
  s0.markLine = { silent: true, symbol: "none", data: [...(((s0.markLine || {}).data) || []), ...data] };
}

// Items 35/39/40: per-series style overrides for line/area/combo (keyed by series name).
export function applySeriesStyles(option, opts) {
  const map = opts.seriesStyle || {};
  if (!Object.keys(map).length || !Array.isArray(option.series)) return;
  option.series = option.series.map((s) => {
    const st = map[s.name];
    if (!st || s.__trend) return s;
    const out = { ...s };
    if (st.color) out.itemStyle = { ...(s.itemStyle || {}), color: st.color };
    if (s.type === "line") {
      out.lineStyle = {
        ...(s.lineStyle || {}),
        ...(st.color ? { color: st.color } : {}),
        ...(Number(st.width) > 0 ? { width: Number(st.width) } : {}),
        ...(st.dash ? { type: st.dash } : {}),
      };
      if (st.smooth === true || st.smooth === false) out.smooth = st.smooth;
      if (st.marker) { out.symbol = st.marker === "none" ? "none" : st.marker; out.showSymbol = st.marker !== "none"; }
      if (Number(st.markerSize) > 0) out.symbolSize = Number(st.markerSize);
      if (st.areaOpacity !== undefined && st.areaOpacity !== "" && out.areaStyle) {
        out.areaStyle = { ...out.areaStyle, opacity: Number(st.areaOpacity) };
      }
    }
    return out;
  });
}

// Least-squares fits: linear, exponential, logarithmic, polynomial (order 2).
function fitFn(kind, pts) {
  const lin = (P) => {
    const n = P.length;
    const sx = P.reduce((a, [x]) => a + x, 0), sy = P.reduce((a, [, y]) => a + y, 0);
    const sxx = P.reduce((a, [x]) => a + x * x, 0), sxy = P.reduce((a, [x, y]) => a + x * y, 0);
    const den = n * sxx - sx * sx;
    if (!den) return null;
    const b = (n * sxy - sx * sy) / den;
    return [(sy - b * sx) / n, b];
  };
  if (kind === "exponential") {
    const P = pts.filter(([, y]) => y > 0).map(([x, y]) => [x, Math.log(y)]);
    if (P.length < 2) return null;
    const c = lin(P);
    return c ? (x) => Math.exp(c[0] + c[1] * x) : null;
  }
  if (kind === "logarithmic") {
    const x0 = Math.min(...pts.map(([x]) => x));
    const c = lin(pts.map(([x, y]) => [Math.log(x - x0 + 1), y]));
    return c ? (x) => c[0] + c[1] * Math.log(x - x0 + 1) : null;
  }
  if (kind === "polynomial") {
    let s1 = 0, s2 = 0, s3 = 0, s4 = 0, t0 = 0, t1 = 0, t2 = 0;
    pts.forEach(([x, y]) => { s1 += x; s2 += x * x; s3 += x ** 3; s4 += x ** 4; t0 += y; t1 += x * y; t2 += x * x * y; });
    const s0 = pts.length;
    const det3 = (m) => m[0] * (m[4] * m[8] - m[5] * m[7]) - m[1] * (m[3] * m[8] - m[5] * m[6]) + m[2] * (m[3] * m[7] - m[4] * m[6]);
    const D = det3([s0, s1, s2, s1, s2, s3, s2, s3, s4]);
    if (!D) return null;
    const a = det3([t0, s1, s2, t1, s2, s3, t2, s3, s4]) / D;
    const b = det3([s0, t0, s2, s1, t1, s3, s2, t2, s4]) / D;
    const c = det3([s0, s1, t0, s1, s2, t1, s2, s3, t2]) / D;
    return (x) => a + b * x + c * x * x;
  }
  const c = lin(pts);
  return c ? (x) => c[0] + c[1] * x : null;
}

// Item 32: trendline per first/all series, 5 fit types, styled line, scatter-aware.
// Batch 7 (item 75): rule-based anomaly markers — z-score outliers pinned on the series.
export function applyAnomalyMarkers(option, threshold = 2.5) {
  (option.series || []).forEach((s) => {
    if (!["line", "bar"].includes(s.type) || !Array.isArray(s.data) || s.data.length < 8) return;
    const vals = s.data.map((d) => {
      const v = typeof d === "object" && d !== null ? d.value : d;
      const n = Array.isArray(v) ? Number(v[1]) : Number(v);
      return Number.isFinite(n) ? n : null;
    });
    const clean = vals.filter((v) => v != null);
    if (clean.length < 8) return;
    const mean = clean.reduce((a, b) => a + b, 0) / clean.length;
    const sd = Math.sqrt(clean.reduce((a, b) => a + (b - mean) ** 2, 0) / clean.length) || 1;
    const marks = [];
    vals.forEach((v, i) => {
      if (v == null) return;
      const z = (v - mean) / sd;
      if (Math.abs(z) >= threshold) {
        marks.push({ coord: [i, v], value: `z=${z.toFixed(1)}`,
          itemStyle: { color: z > 0 ? "#DC2626" : "#7C3AED" } });
      }
    });
    if (marks.length) {
      s.markPoint = { ...(s.markPoint || {}), symbol: "pin", symbolSize: 30,
        label: { show: false },
        tooltip: { formatter: (p) => `Anomaly: ${p.data?.value || ""}` },
        data: [...(s.markPoint?.data || []), ...marks] };
    }
  });
}

export function applyTrendline(option, opts) {
  const t = opts.trendline || {};
  if (!t.enabled || !Array.isArray(option.series) || !option.series.length) return;
  const candidates = option.series.filter((s) => ["bar", "line", "scatter"].includes(s.type) && Array.isArray(s.data) && !s.__trend);
  const chosen = t.series === "all" ? candidates : candidates.slice(0, 1);
  if (!chosen.length) return;
  const round2 = (v) => (Number.isFinite(v) ? Math.round(v * 100) / 100 : null);
  const kind = t.type || "linear";
  const added = [];
  chosen.forEach((s) => {
    const base = {
      name: t.series === "all" && chosen.length > 1 ? `${s.name || "Series"} trend` : (t.label || "Trend"),
      type: "line", showSymbol: false, silent: true, z: 5, __trend: true, smooth: kind === "moving_avg",
      lineStyle: { color: t.color || "#9AA3B8", width: Number(t.width) || 2, type: t.dash || "dashed" },
      itemStyle: { color: t.color || "#9AA3B8" },
      ...(s.yAxisIndex != null ? { yAxisIndex: s.yAxisIndex } : {}),
      ...(s.xAxisIndex != null ? { xAxisIndex: s.xAxisIndex } : {}),
    };
    if (s.type === "scatter") {
      const pts = s.data
        .map((d) => { const v = Array.isArray(d) ? d : d && typeof d === "object" ? d.value : null; return Array.isArray(v) ? [Number(v[0]), Number(v[1])] : null; })
        .filter((p) => p && Number.isFinite(p[0]) && Number.isFinite(p[1]))
        .sort((a, b) => a[0] - b[0]);
      if (pts.length < 2) return;
      if (kind === "moving_avg") {
        const w = Math.max(2, Math.min(pts.length, Number(t.window) || 3));
        base.data = pts.map((_, i) => { const seg = pts.slice(Math.max(0, i - w + 1), i + 1); return [pts[i][0], round2(seg.reduce((a, p) => a + p[1], 0) / seg.length)]; });
      } else {
        const f = fitFn(kind, pts);
        if (!f) return;
        base.data = pts.map(([x]) => [x, round2(f(x))]);
      }
      added.push(base);
      return;
    }
    const ys = s.data.map((d) => Number(d && typeof d === "object" && !Array.isArray(d) ? d.value : d));
    if (ys.length < 2) return;
    if (kind === "moving_avg") {
      const w = Math.max(2, Math.min(ys.length, Number(t.window) || 3));
      base.data = ys.map((_, i) => { const seg = ys.slice(Math.max(0, i - w + 1), i + 1).filter(Number.isFinite); return seg.length ? round2(seg.reduce((a, b) => a + b, 0) / seg.length) : null; });
    } else {
      const pts = ys.map((y, x) => [x, y]).filter(([, y]) => Number.isFinite(y));
      if (pts.length < 2) return;
      const f = fitFn(kind, pts);
      if (!f) return;
      base.data = ys.map((_, x) => round2(f(x)));
    }
    added.push(base);
  });
  if (added.length) option.series = [...option.series, ...added];
}

// ---- Item 86: unified advanced color modes (default | range | rule | category | lmh) ----
export function advColorMode(opts) {
  const m = opts.advColorMode;
  if (m && m !== "auto") return m;
  return (opts.rangeColors || []).some((r) => r && r.start !== "" && r.start != null) ? "range" : "default";
}

// Returns (value, category) -> {color,...} | null, or null when the mode has nothing to apply.
export function advStyleResolver(opts, mm = {}) {
  const mode = advColorMode(opts);
  if (mode === "range") {
    const ranges = activeRanges(opts);
    if (!ranges.length) return null;
    return (v) => rangeStyleOf(v, ranges);
  }
  if (mode === "rule") {
    const rules = (opts.colorRules || []).filter((r) => r && r.color && r.op && r.enabled !== false);
    if (!rules.length) return null;
    return (v) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return null;
      for (const r of rules) {
        const a = Number(r.value), b = Number(r.value2);
        const ok = r.op === "gt" ? n > a : r.op === "gte" ? n >= a : r.op === "lt" ? n < a : r.op === "lte" ? n <= a
          : r.op === "eq" ? n === a : r.op === "ne" ? n !== a
          : r.op === "between" ? (Number.isFinite(a) && Number.isFinite(b) && n >= Math.min(a, b) && n <= Math.max(a, b)) : false;
        if (ok) return { color: r.color };
      }
      return null;
    };
  }
  if (mode === "category") {
    const map = opts.categoryColors || {};
    const keys = Object.keys(map).filter((k) => map[k]);
    if (!keys.length) return null;
    const lower = Object.fromEntries(keys.map((k) => [k.toLowerCase(), map[k]]));
    return (v, cat) => {
      if (cat == null) return null;
      const c = map[String(cat)] || lower[String(cat).toLowerCase()];
      return c ? { color: c } : null;
    };
  }
  if (mode === "lmh") {
    const l = opts.lmh || {};
    const low = l.low || "#EF4444", mid = l.mid || "#FFB020", high = l.high || "#22C55E";
    const min = Number(mm.min), max = Number(mm.max);
    if (!Number.isFinite(min) || !Number.isFinite(max) || max === min) return null;
    return (v) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return null;
      const t = Math.max(0, Math.min(1, (n - min) / (max - min)));
      return { color: t <= 0.5 ? lerpColor(low, mid, t * 2) : lerpColor(mid, high, (t - 0.5) * 2) };
    };
  }
  return null;
}
